import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Collapse,
  Divider,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import color from "../../../Constants/colors";
import { userEvaluationRequests } from "../../../Models/userEvaluationRequests";
import MetadataModal from "./MetaDataModal";
import { useUserEvaluationRequestLog } from "../../../Query/useUserEvaluationRequests";
import { downloadDocumentBlob } from "../../../Helpers/documentHelper";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";

interface LogCardDetailsProps {
  expanded: boolean;
  evaluationRequest: userEvaluationRequests;
}

const LogCardDetails: React.FC<LogCardDetailsProps> = ({
  expanded,
  evaluationRequest,
}) => {
  const { t } = useTranslation("actionLog");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isModalOpen, setIsModalOpen] = useState(false);

  const logContainerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, refetch, isFetching, dataUpdatedAt } =
    useUserEvaluationRequestLog(evaluationRequest.id, expanded);
  const logLines = data?.logs ? data.logs.trim().split("\n") : [];

  const lastRefreshedAt = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  const formatDuration = (seconds: number | undefined): string => {
    if (seconds === undefined) return "/";
    const hrs =
      Math.floor(seconds / 3600) == 0 ? "" : Math.floor(seconds / 3600) + "h";
    const mins =
      Math.floor((seconds % 3600) / 60) == 0
        ? ""
        : Math.floor((seconds % 3600) / 60) + "m";
    const secs = seconds % 60 == 0 ? "" : (seconds % 60) + "s";
    return `${hrs} ${mins} ${secs}`;
  };

  const handleDownloadJson = () => {
    if (!data) return;
    const pseudoResponse = {
      data: logLines.join("\n"),
    } as unknown as AxiosResponse;
    downloadDocumentBlob(
      pseudoResponse,
      `evaluation-log-${evaluationRequest.id}.log`
    );
  };

  const stats = [
    {
      label: t("logs.startTime"),
      value: evaluationRequest.metadata?.created_at,
    },
    {
      label: t("logs.endTime"),
      value: evaluationRequest.metadata?.finished_at,
    },
    {
      label: t("logs.duration"),
      value: formatDuration(evaluationRequest.metadata?.duration_seconds),
    },
    {
      label: t("logs.tokenCount"),
      value: evaluationRequest.metadata?.tokens_total,
    },
    {
      label: t("logs.totalCost"),
      value:
        evaluationRequest.metadata?.cost_total !== undefined &&
        evaluationRequest.metadata?.cost_total !== null
          ? `$${evaluationRequest.metadata?.cost_total.toFixed(2)}`
          : "/",
    },
  ];

  useEffect(() => {
    if (expanded && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [expanded, logLines]);

  return (
    <>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            padding: 2,
            backgroundColor: color.white,
            borderTop: `1px solid ${color.primary_light}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            {/* Stats Grid */}
            <Grid container spacing={2} sx={{ flex: 1 }}>
              {stats.map((item) => (
                <Grid item xs={6} md={2.4} key={item.label}>
                  <Typography
                    variant="caption"
                    color={color.primary_dark}
                    display="block"
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    color={color.primary_dark}
                  >
                    {item.value || "/"}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            {evaluationRequest.metadata && (
              <Button
                onClick={() => setIsModalOpen(true)}
                variant="contained"
                fullWidth={isMobile}
                sx={{
                  backgroundColor: color.accent,
                  color: color.white,
                  alignSelf: isMobile ? "stretch" : "flex-end",
                  "&:hover": { backgroundColor: color.primary_dark },
                }}
              >
                {t("logs.viewMetadata")}
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" alignItems="center" mb={1}>
            <Typography
              variant="body2"
              fontWeight="bold"
              color={color.primary_dark}
            >
              {t("logs.executionLog")}
            </Typography>
            <Tooltip title={t("logs.refresh")} arrow>
              <IconButton
                size="small"
                onClick={() => refetch()}
                disabled={isFetching}
                sx={{ color: color.primary_dark, ml: 1 }}
              >
                {isFetching && !isLoading ? (
                  <CircularProgress size={16} sx={{ color: color.primary_dark }} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title={t("logs.downloadJson")} arrow>
              <IconButton
                size="small"
                onClick={handleDownloadJson}
                sx={{ color: color.primary_dark, ml: 1 }}
              >
                <DownloadIcon fontSize="small" color="warning" />
              </IconButton>
            </Tooltip>
            <Box sx={{ flexGrow: 1 }} />
            {lastRefreshedAt && (
              <Typography variant="caption" color={color.secondary}>
                {t("logs.refreshedAt", { time: lastRefreshedAt })}
              </Typography>
            )}
          </Box>

          <Box
            ref={logContainerRef}
            sx={{
              backgroundColor: color.secondaryDark,
              padding: 1.5,
              borderRadius: 1,
              fontFamily: "monospace",
              maxHeight: 300,
              overflowY: "auto",
              border: `1px solid ${color.primary_light}`,
            }}
          >
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : logLines.length > 0 ? (
              logLines.map((logLine, index) => (
                <Typography
                  key={index}
                  variant="caption"
                  component="pre"
                  sx={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                    color: color.primary_dark,
                    fontSize: "0.7rem",
                    lineHeight: 1.4,
                  }}
                >
                  {logLine}
                </Typography>
              ))
            ) : (
              <Typography variant="caption" color="textSecondary">
                {t("logs.noLogs")}
              </Typography>
            )}
          </Box>
        </Box>
      </Collapse>

      <MetadataModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        metadata={evaluationRequest.metadata}
      />
    </>
  );
};

export default LogCardDetails;
